import type { BotDetail, BotState, LogLevel, McAccountStatus, XYZ } from './bot'
import type { RunnerLoad } from './runner-protocol'

/**
 * Контракт socket.io между API и браузером (docs/06 §8).
 * Путь `/ws`, аутентификация — access-JWT в `auth.token` при handshake.
 *
 * Интерфейсы (а не zod-схемы) сознательно: они подставляются в дженерики
 * socket.io — `Server<ClientEvents, ServerEvents>` / `Socket<ServerEvents, ClientEvents>`,
 * и там нужны именно типы функций.
 */

/** Имена комнат. Проверка прав при `subscribe` — та же логика, что в REST-guard. */
export const WS_PATH = '/ws'
export const wsRoom = {
  org: (orgId: string) => `org:${orgId}`,
  bot: (botId: string) => `bot:${botId}`,
  viewer: (sessionId: string) => `viewer:${sessionId}`
} as const

export interface ServerEvents {
  'bot.state': (e: { botId: string; state: BotState; detail?: BotDetail; at: string }) => void
  'bot.event': (e: {
    botId: string
    level: LogLevel
    kind: string
    message: string
    data?: unknown
    at: string
  }) => void
  /**
   * Батч логов: на активном сервере чат идёт десятками строк в секунду,
   * поэтому события пакуются (docs/06 §8). Клиент обязан поддерживать оба:
   * одиночные события приходят при низком трафике.
   */
  'bot.event.batch': (events: Array<Parameters<ServerEvents['bot.event']>[0]>) => void
  'bot.command.ack': (e: { commandId: string; botId: string; ok: boolean; error?: string }) => void
  'bot.deleted': (e: { botId: string }) => void

  'mcAccount.authCode': (e: {
    accountId: string
    userCode: string
    verificationUri: string
    expiresAt: string
  }) => void
  'mcAccount.status': (e: { accountId: string; status: McAccountStatus; error?: string }) => void

  'runner.status': (e: { runnerId: string; online: boolean; load?: RunnerLoad }) => void
  'dashboard.tick': (e: { botsPlaying: number; botsTotal: number; at: string }) => void

  // камера (бинарные payload'ы, msgpack)
  'viewer.chunk': (e: Uint8Array) => void // msgpack ViewerChunkFrame
  'viewer.unloadChunk': (e: { sessionId: string; x: number; z: number }) => void
  'viewer.entities': (e: Uint8Array) => void // msgpack ViewerEntitiesFrame
  'viewer.position': (e: {
    sessionId: string
    pos: XYZ
    yaw: number
    pitch: number
    addMesh: boolean
  }) => void
  'viewer.blockUpdate': (e: Uint8Array) => void // msgpack ViewerBlockUpdateFrame
  'viewer.closed': (e: { sessionId: string; reason: string }) => void
  /**
   * Мини-карта (L1, docs/08 §4) — msgpack `MinimapFrame`, комната `bot:<botId>`.
   *
   * Дополнение к docs/06 §8: там события для L1 нет, хотя docs/08 §4 и
   * docs/10 §4 требуют мини-карту в карточке бота. Своя сессия для неё не
   * нужна (нет ни WorldView, ни viewDistance), поэтому кадр едет в комнату бота.
   */
  'viewer.minimap': (e: Uint8Array) => void
}

export interface ClientEvents {
  subscribe: (p: { room: string }, ack: (r: { ok: boolean; error?: string }) => void) => void
  unsubscribe: (p: { room: string }) => void
  /**
   * Сцена смонтирована и слушает кадры — можно грузить мир.
   *
   * Отдельное событие, а не «первый вошедший в комнату», потому что вход в
   * комнату происходит РАНЬШЕ, чем появляется потребитель: три.js приезжает
   * ленивым чанком бандла. Runner на старт высыпает весь мир за секунду, и на
   * проде эта пачка уходила в сокет до того, как канвас успевал повесить
   * обработчик, — камера оставалась чёрной навсегда, потому что дальше чанки
   * шлются только при движении бота.
   */
  'viewer.ready': (p: { sessionId: string }) => void
  'viewer.heartbeat': (p: { sessionId: string }) => void
  'viewer.setViewDistance': (p: { sessionId: string; viewDistance: number }) => void
}

/** Батч логов: docs/06 §8 — до 50 сообщений в одном фрейме. */
export const WS_EVENT_BATCH_MAX = 50

/** Backpressure: >4 МБ в буфере сокета → закрываем viewer-сессию. */
export const WS_BUFFER_LIMIT_BYTES = 4 * 1024 * 1024

/**
 * Причины закрытия viewer-сессии (`viewer.closed.reason`, docs/05 §2 `endReason`).
 *
 * `no-subscribers` добавлена к списку из docs/05 §2: docs/08 §3.2 закрывает
 * сессию именно с этой причиной, когда в комнате не осталось сокетов, и без неё
 * `endReason` в БД не сходился бы с тем, что реально произошло.
 */
export const VIEWER_CLOSE_REASONS = [
  'client-close',
  'heartbeat-timeout',
  'slow-consumer',
  'bot-stopped',
  'no-subscribers'
] as const
export type ViewerCloseReason = (typeof VIEWER_CLOSE_REASONS)[number]
